import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useViewport } from "@/hooks/useViewport";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";
import usePageTracking from "@/hooks/usePageTracking";
const FeedbackWidget = lazy(() => import("@/components/FeedbackWidget"));
import BottomNav from "@/components/BottomNav";
import ResumeStrip from "@/components/ResumeStrip";
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import ErrorBoundary from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
const InstallPrompt = lazy(() => import("@/components/InstallPrompt"));
import { BillingProvider, useBilling } from "@/context/BillingContext";
import { EditionProvider } from "@/context/EditionContext";
import { setRuntimeTranslations } from "@/lib/localizeContent";
import api from "@/lib/api";
import RequirePaid from "@/components/billing/RequirePaid";
import UpgradeDialog from "@/components/billing/UpgradeDialog";

// Heavy / authenticated routes are code-split so the landing + auth bundle stays small on mobile.
const Learning = lazy(() => import("@/pages/Learning"));
const CodeDrill = lazy(() => import("@/pages/CodeDrill"));
const Workspace = lazy(() => import("@/pages/Workspace"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SharedProject = lazy(() => import("@/pages/SharedProject"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const Profile = lazy(() => import("@/pages/Profile"));
const Instructor = lazy(() => import("@/pages/Instructor"));
const Guides = lazy(() => import("@/pages/Guides"));
const WeeklyArticle = lazy(() => import("@/pages/WeeklyArticle"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const Inspections = lazy(() => import("@/pages/Inspections"));
const InspectionRun = lazy(() => import("@/pages/InspectionRun"));
const Certification = lazy(() => import("@/pages/Certification"));
const KnowledgeCheck = lazy(() => import("@/pages/KnowledgeCheck"));
const Simulator = lazy(() => import("@/pages/Simulator"));
const Team = lazy(() => import("@/pages/Team"));
const TeamJoin = lazy(() => import("@/pages/TeamJoin"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const ScenarioArticle = lazy(() => import("@/pages/ScenarioArticle"));
const CodeSectionPage = lazy(() => import("@/pages/CodeSectionPage"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));

// Mount non-critical widgets (feedback, cookie banner, install prompt) only once the browser is idle,
// so they never compete with first paint on slow connections.
function useIdle(delay = 1500) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if ("requestIdleCallback" in window) { const id = window.requestIdleCallback(() => setIdle(true), { timeout: delay }); return () => window.cancelIdleCallback(id); }
    const t = setTimeout(() => setIdle(true), delay); return () => clearTimeout(t);
  }, [delay]);
  return idle;
}

const PageSpinner = () => <div className="min-h-[60vh] grid place-items-center bg-background" data-testid="route-loading"><Loader2 className="animate-spin text-primary" size={32} /></div>;

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "administrator") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireInstructor({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "instructor" && user.role !== "administrator") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireSupervisor({ children }) {
  const { user, loading } = useAuth();
  const billing = useBilling();
  if (loading || user === null || (user && billing.loading)) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!billing.team_access) return <RequirePaid feature="team">{children}</RequirePaid>;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  usePageTracking();
  useEffect(() => { api.get("/translations/es/guides").then(({ data }) => setRuntimeTranslations("es", data)).catch(() => {}); }, []);
  const { isMobile, reduceMotion } = useViewport();
  const idle = useIdle();
  // Process the Google OAuth session_id fragment FIRST, before any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isWorkspace = location.pathname.startsWith("/workspace");
  const hideBottomNav = isWorkspace;

  const routes = (
    <Suspense fallback={<PageSpinner />}>
    <Routes location={location}>
        <Route path="/" element={<Landing />} />
        <Route path="/learn" element={<Learning />} />
        <Route path="/learn/scenarios" element={<ScenarioArticle />} />
        <Route path="/learn/:slug" element={<ScenarioArticle />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/weekly/:slug" element={<WeeklyArticle />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/glossary/:slug" element={<Glossary />} />
        <Route path="/es/casos" element={<ScenarioArticle forceLang="es" />} />
        <Route path="/es/casos/:slug" element={<ScenarioArticle forceLang="es" />} />
        <Route path="/code" element={<CodeSectionPage />} />
        <Route path="/code/:slug" element={<CodeSectionPage />} />
        <Route path="/es/codigo" element={<CodeSectionPage forceLang="es" />} />
        <Route path="/es/codigo/:slug" element={<CodeSectionPage forceLang="es" />} />
        <Route path="/es/glosario" element={<Glossary forceLang="es" />} />
        <Route path="/es/glosario/:slug" element={<Glossary forceLang="es" />} />
        <Route path="/es/guias" element={<Guides forceLang="es" />} />
        <Route path="/es/guias/:slug" element={<Guides forceLang="es" />} />
        <Route path="/guides/:slug" element={<Guides />} />
        <Route path="/inspections" element={<RequireAuth><Inspections /></RequireAuth>} />
        <Route path="/drill" element={<RequireAuth><CodeDrill /></RequireAuth>} />
        <Route path="/inspections/:id" element={<RequireAuth><InspectionRun /></RequireAuth>} />
        <Route path="/certification" element={<Certification />} />
        <Route path="/certification/:stateSlug" element={<Certification />} />
        <Route path="/quiz" element={<KnowledgeCheck />} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/workspace" element={<RequirePaid feature="workspace"><Workspace /></RequirePaid>} />
        <Route path="/workspace/:id" element={<RequirePaid feature="workspace"><Workspace /></RequirePaid>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/success" element={<RequireAuth><BillingSuccess /></RequireAuth>} />
        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/welcome" element={<RequireAuth><Welcome /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        <Route path="/instructor" element={<RequireInstructor><Instructor /></RequireInstructor>} />
        <Route path="/team" element={<RequireSupervisor><Team /></RequireSupervisor>} />
        <Route path="/team/join" element={<RequireAuth><TeamJoin /></RequireAuth>} />
        <Route path="/admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/shared/:id" element={<SharedProject />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );

  // Slide/fade page transitions. The CAD Workspace is excluded (its fixed
  // toolbars/drawers must not sit inside an animated transform). Mobile gets a
  // slightly larger, snappier vertical slide; reduced-motion users get none.
  const duration = reduceMotion ? 0 : isMobile ? 0.26 : 0.32;
  const offset = reduceMotion ? 0 : isMobile ? 20 : 10;
  const variants = {
    initial: { opacity: 0, y: offset },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -offset },
  };

  return (
    <>
      <OfflineBanner />
      {isWorkspace ? (
        routes
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
          >
            {routes}
          </motion.div>
        </AnimatePresence>
      )}
      {idle && (
        <Suspense fallback={null}>
          <FeedbackWidget />
          {!isWorkspace && <CookieConsent />}
          <InstallPrompt />
        </Suspense>
      )}
      <UpgradeDialog />
      {!hideBottomNav && <div aria-hidden className="lg:hidden h-14" />}
      {!hideBottomNav && idle && <ResumeStrip />}
      {!hideBottomNav && <BottomNav />}
    </>
  );
}

function LocaleGreeting() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Greet each visitor once in their auto-detected browser language.
    if (localStorage.getItem("fab_greeted")) return;
    const timer = setTimeout(() => {
      toast(t("common.welcomeGreeting"));
      localStorage.setItem("fab_greeted", "1");
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BillingProvider>
        <EditionProvider>
        <BrowserRouter>
          <LocaleGreeting />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        </EditionProvider>
        </BillingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
